import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosConfig';
import ColumnFilter from '../common/ColumnFilter';
import EmployeeDetailModal from './EmployeeDetailModal';
import LiveStatusStrip from './LiveStatusStrip';
import { COLUMN_DEFS, optionsFor, applyFilters, applySort } from './reportTable';
import useAutoRefresh, { UpdatedAgo } from './useAutoRefresh';
import { utilisationTone, downloadBlob } from './format';

/**
 * The daily and monthly reports differ only in their period control and two
 * columns, so they share one component rather than two near-identical files —
 * a fix to the fetch, export or empty state can only be made in one place.
 */

const todayISO = () => new Date().toISOString().slice(0, 10);
const thisMonthISO = () => new Date().toISOString().slice(0, 7);

export default function ReportTab({ mode }) {
    const monthly = mode === 'monthly';
    const columns = COLUMN_DEFS[monthly ? 'monthly' : 'daily'];

    const [period, setPeriod] = useState(monthly ? thisMonthISO() : todayISO());
    const [department, setDepartment] = useState('all');
    const [data, setData] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);

    const [filters, setFilters] = useState({});
    const [sort, setSort] = useState(null);
    const [hideUntracked, setHideUntracked] = useState(false);

    // `silent` skips the loading state. A background poll must not blank the
    // table every 60 seconds — the rows are still valid until the new ones
    // arrive, and a table that flashes empty on a timer is unusable.
    const load = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        setError('');
        return axiosInstance
            .get(`/api/working-time/${monthly ? 'monthly' : 'daily'}`, {
                params: monthly ? { month: period, department } : { date: period, department }
            })
            .then((r) => {
                setData(r.data);
                setDepartments(r.data.departments || []);
            })
            .catch((e) => setError(e.response?.data?.message || 'Could not load the report'))
            .finally(() => setLoading(false));
    }, [monthly, period, department]);

    useEffect(() => { load(); }, [load]);

    // Only today's figures can still move. A past date and the whole monthly
    // view are frozen, so polling them refetches an unchanging answer.
    const isLive = !monthly && period === todayISO();
    const lastUpdated = useAutoRefresh(load, { enabled: isLive });

    // Changing the period or the department reloads a different set of rows, so
    // filters chosen against the old set would silently hide the new one.
    useEffect(() => { setFilters({}); setSort(null); }, [period, department]);

    const allRows = useMemo(() => (data && data.rows) || [], [data]);

    const visibleRows = useMemo(() => {
        const tracked = hideUntracked ? allRows.filter((r) => r.tracked) : allRows;
        return applySort(applyFilters(tracked, filters), sort);
    }, [allRows, hideUntracked, filters, sort]);

    // The value list for each panel is computed from the rows the OTHER filters
    // leave behind, so options narrow as you filter — the same as Excel and as
    // Customer Register's server-side equivalent.
    const optionsByKey = useMemo(() => {
        const result = {};
        columns.forEach((c) => {
            const others = { ...filters };
            delete others[c.key];
            const base = hideUntracked ? allRows.filter((r) => r.tracked) : allRows;
            result[c.key] = optionsFor(applyFilters(base, others), c.key);
        });
        return result;
    }, [columns, filters, allRows, hideUntracked]);

    const setFilter = (key, values) => setFilters((prev) => ({ ...prev, [key]: values }));
    const clearFilters = () => { setFilters({}); setSort(null); };

    const toggleSort = (key) => setSort((prev) => {
        if (!prev || prev.key !== key) return { key, direction: 'asc' };
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        return null;
    });

    const filterCount = Object.values(filters).filter((v) => v && v.length).length;
    const isFiltered = filterCount > 0 || hideUntracked;

    const download = (format) => {
        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        const params = monthly
            ? { month: period, department, format }
            : { date: period, department, format };

        // Filters run in the browser, so the export is told which rows are on
        // screen. Without it a download silently re-widens to every employee.
        if (isFiltered) params.userIds = visibleRows.map((r) => r.userId).join(',');

        axiosInstance
            .get(`/api/working-time/${monthly ? 'monthly' : 'daily'}/export`, {
                params, responseType: 'blob'
            })
            .then((r) => downloadBlob(r.data, `working-time-${period}.${extension}`))
            .catch(() => setError('Could not download the export'));
    };

    const totals = data && data.totals;
    const idleMinutes = Math.round(((data && data.settings && data.settings.idleThresholdSeconds) || 300) / 60);

    return (
        <div>
            <div className="wt-toolbar">
                <div className="wt-field">
                    <label htmlFor="wt-period">{monthly ? 'Month' : 'Date'}</label>
                    <input
                        id="wt-period"
                        type={monthly ? 'month' : 'date'}
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                    />
                </div>
                <div className="wt-field">
                    <label htmlFor="wt-dept">Department</label>
                    <select
                        id="wt-dept"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                    >
                        <option value="all">All departments</option>
                        {departments.map((d) => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                    </select>
                </div>
                <label className="wt-check">
                    <input
                        type="checkbox"
                        checked={hideUntracked}
                        onChange={(e) => setHideUntracked(e.target.checked)}
                    />
                    Hide employees with no agent data
                </label>
                {/* Wrapped, not `onClick={load}` — React would pass the click
                    event as load's first argument, silencing the spinner. */}
                <button type="button" className="wt-btn" onClick={() => load()}>Refresh</button>
                {isFiltered && (
                    <button type="button" className="wt-btn" onClick={clearFilters}>
                        Clear filters
                    </button>
                )}
                <button type="button" className="wt-btn primary" onClick={() => download('xlsx')}>
                    Excel
                </button>
                <button type="button" className="wt-btn" onClick={() => download('pdf')}>
                    PDF
                </button>
                {isLive && <UpdatedAgo at={lastUpdated} />}
            </div>

            {error && <div className="wt-error">{error}</div>}

            {!monthly && data && data.liveStatus && <LiveStatusStrip counts={data.liveStatus} />}

            {totals && (
                <div className="wt-stats">
                    <div className="wt-stat">
                        <div className="label">Employees</div>
                        <div className="value">{totals.headcount}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Tracked</div>
                        <div className="value">{totals.tracked}</div>
                        <div className="hint">{totals.notTracked} without agent data</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Active hours</div>
                        <div className="value">{totals.activeHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Idle hours</div>
                        <div className="value">{totals.idleHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div
                            className="label"
                            title="Time the agent was locked/off or otherwise not actively working."
                        >
                            Away
                        </div>
                        <div className="value">{totals.awayHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Total working hours</div>
                        <div className="value">{totals.totalHours}</div>
                    </div>
                    <div className="wt-stat">
                        <div className="label">Active share</div>
                        <div className="value">{totals.utilisation}%</div>
                        <div className="hint">active vs total</div>
                    </div>
                </div>
            )}

            <div className="wt-table-wrap">
                <table className="wt-table">
                    <thead>
                        <tr>
                            {columns.map((c) => {
                                const arrow = sort && sort.key === c.key
                                    ? (sort.direction === 'asc' ? '▲' : '▼')
                                    : '';
                                return (
                                    <th key={c.key}>
                                        <span className="wt-th-inner">
                                            <button
                                                type="button"
                                                className="wt-sort"
                                                onClick={() => toggleSort(c.key)}
                                                title={c.title || `Sort by ${c.label}`}
                                            >
                                                {c.label}
                                                {arrow && <span className="wt-sort-arrow"> {arrow}</span>}
                                            </button>
                                            <ColumnFilter
                                                column={c.key}
                                                label={c.label}
                                                selected={filters[c.key] || []}
                                                onChange={(values) => setFilter(c.key, values)}
                                                options={optionsByKey[c.key]}
                                            />
                                        </span>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={columns.length} className="wt-empty">Loading…</td></tr>
                        )}
                        {!loading && !visibleRows.length && (
                            <tr>
                                <td colSpan={columns.length} className="wt-empty">
                                    No employees match this filter.
                                </td>
                            </tr>
                        )}
                        {!loading && visibleRows.map((r) => (
                            <tr key={r.userId} className={r.tracked ? '' : 'untracked'}>
                                <td>
                                    <button type="button" className="wt-name" onClick={() => setSelected(r)}>
                                        {r.name}
                                    </button>
                                    <span className="wt-sub">{r.employeeCode}</span>
                                </td>
                                <td>{r.department || '-'}</td>
                                {monthly && <td className="wt-mono">{r.daysTracked || 0}</td>}
                                <td className="wt-mono">{r.firstActiveTime || '-'}</td>
                                <td className="wt-mono">{r.lastActiveTime || '-'}</td>
                                <td className="wt-mono"><strong>{r.activeHours}</strong></td>
                                <td className="wt-mono">{r.idleHours}</td>
                                <td className="wt-mono">{r.awayHours}</td>
                                <td className="wt-mono"><strong>{r.totalHours}</strong></td>
                                {monthly && <td className="wt-mono">{r.averageActive}</td>}
                                <td>
                                    {r.tracked
                                        ? (
                                            <span className={`wt-badge ${utilisationTone(r.utilisation)}`}>
                                                {r.utilisation}%
                                            </span>
                                        )
                                        : <span className="wt-badge muted">No data</span>}
                                </td>
                                {!monthly && <td className="wt-mono">{r.deviceCount || '-'}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isFiltered && (
                <p className="wt-caption">
                    Showing {visibleRows.length} of {allRows.length} employees. The figures above
                    cover all {allRows.length} — an export covers the {visibleRows.length} shown.
                </p>
            )}

            <p className="wt-note">
                Active time is measured from keyboard and mouse activity only. After{' '}
                <strong>{idleMinutes}</strong> minutes without input the agent marks the employee
                idle and stops counting active time. Away is screen-locked, signed-out, sleeping or
                shut-down time — shown so the gap between Total and the working day is never a
                mystery, but it is not counted toward it. Total working hours = active + idle.
            </p>

            {selected && (
                <EmployeeDetailModal
                    employee={selected}
                    month={monthly ? period : period.slice(0, 7)}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
}
